// Structured data for the Secure AI Adoption site. src/router/securityHead.js writes it into
// <head> on every route (and the build-time prerender bakes it into the HTML). It never carries
// prices or offers: the price list is sent only on request.
import { CANONICAL_ORIGIN } from './seo';
import { SERVICES } from './services';
import { ASSESSMENT } from './assessment';

const ORG_ID = `${CANONICAL_ORIGIN}/#org`;
const SITE_ID = `${CANONICAL_ORIGIN}/#website`;

// Official profiles, as linked from the site footer (SecurityGlobalFooter.vue).
const SAME_AS = [
  'https://www.linkedin.com/company/digu-official/',
  'https://www.instagram.com/digu.official/',
  'https://www.tiktok.com/@digu.official',
  'https://www.facebook.com/digu.official/',
  'https://x.com/digu_official',
  'https://www.youtube.com/@digu_official',
];

export const ORGANIZATION = {
  '@type': 'Organization',
  '@id': ORG_ID,
  name: 'DIGU',
  legalName: 'DIGU, LLC',
  url: `${CANONICAL_ORIGIN}/`,
  logo: {
    '@type': 'ImageObject',
    url: `${CANONICAL_ORIGIN}/images/digu-logo.png`,
  },
  description:
    'DIGU secures AI coding agents inside engineering organizations. DIGU is Certified in the Claude Partner Network and is an Okta partner.',
  knowsAbout: [
    'AI coding agent security',
    'Claude Code security',
    'GitHub Copilot security',
    'Cursor security',
    'OWASP Top 10 for Agentic Applications',
    'NIST AI Risk Management Framework',
    'ISO/IEC 42001',
  ],
  // Contact details as published on the contact page (and the phone number in the footer).
  address: {
    '@type': 'PostalAddress',
    streetAddress: '166 Geary Str STE 1500 #1230',
    addressLocality: 'San Francisco',
    addressRegion: 'CA',
    postalCode: '94108',
    addressCountry: 'US',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'sales',
    email: 'sales@digu.one',
    telephone: '+1-833-596-3361',
    url: `${CANONICAL_ORIGIN}/contact`,
  },
  sameAs: SAME_AS,
};

export const WEBSITE = {
  '@type': 'WebSite',
  '@id': SITE_ID,
  name: 'DIGU',
  url: `${CANONICAL_ORIGIN}/`,
  publisher: { '@id': ORG_ID },
  inLanguage: 'en',
};

const service = (path, name, description) => ({
  '@type': 'Service',
  '@id': `${CANONICAL_ORIGIN}${path}#${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
  name,
  description,
  serviceType: 'AI coding agent security',
  provider: { '@id': ORG_ID },
  areaServed: 'Worldwide',
  url: `${CANONICAL_ORIGIN}${path}`,
});

// The engagements, from the same copy the Services and Assessment pages render.
export const servicesJsonLd = (path) => [
  service(path, SERVICES.frontDoor.title, SERVICES.frontDoor.paragraphs[0]),
  ...SERVICES.ladder.rows.map(([name, outcome]) => service(path, name.strong, outcome)),
];

export const assessmentJsonLd = (path) => [
  service(path, SERVICES.frontDoor.title, ASSESSMENT.hero.lede),
];

// trail: [{ name, path }] from the home page down to the current page.
export const breadcrumbJsonLd = (trail) => ({
  '@type': 'BreadcrumbList',
  itemListElement: trail.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: `${CANONICAL_ORIGIN}${item.path}`,
  })),
});

export const graph = (nodes) => ({ '@context': 'https://schema.org', '@graph': nodes });

export const faqPageJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
});
